import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, Phone, Globe, Mail } from "lucide-react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/motion";
import DealerMap from "@/components/DealerMap";

const STATE_NAMES = { al:"Alabama",ak:"Alaska",az:"Arizona",ar:"Arkansas",ca:"California",co:"Colorado",ct:"Connecticut",de:"Delaware",fl:"Florida",ga:"Georgia",hi:"Hawaii",id:"Idaho",il:"Illinois",in:"Indiana",ia:"Iowa",ks:"Kansas",ky:"Kentucky",la:"Louisiana",me:"Maine",md:"Maryland",ma:"Massachusetts",mi:"Michigan",mn:"Minnesota",ms:"Mississippi",mo:"Missouri",mt:"Montana",ne:"Nebraska",nv:"Nevada",nh:"New Hampshire",nj:"New Jersey",nm:"New Mexico",ny:"New York",nc:"North Carolina",nd:"North Dakota",oh:"Ohio",ok:"Oklahoma",or:"Oregon",pa:"Pennsylvania",ri:"Rhode Island",sc:"South Carolina",sd:"South Dakota",tn:"Tennessee",tx:"Texas",ut:"Utah",vt:"Vermont",va:"Virginia",wa:"Washington",wv:"West Virginia",wi:"Wisconsin",wy:"Wyoming",dc:"District of Columbia" };
const STATE_CODES = Object.keys(STATE_NAMES);

export default function DealerLocatorPage() {
  const [locations, setLocations] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/dealer-directory")
      .then((r) => setLocations(r.data.locations || []))
      .catch(() => setLocations([]));
  }, []);

  // Structured data so search engines and AI assistants can read the dealer network
  useEffect(() => {
    if (!locations || locations.length === 0) return;
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "The Banner Bar Authorized Dealer Directory",
      itemListElement: locations.map((l, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "LocalBusiness",
          name: l.company,
          telephone: l.phone || undefined,
          url: l.website || undefined,
          address: {
            "@type": "PostalAddress",
            streetAddress: l.address || undefined,
            addressLocality: l.city || undefined,
            addressRegion: l.state || undefined,
            postalCode: l.zip || undefined,
            addressCountry: "US",
          },
        },
      })),
    };
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-dealer-directory", "1");
    s.text = JSON.stringify(jsonLd);
    document.head.appendChild(s);
    return () => s.remove();
  }, [locations]);

  const filtered = useMemo(() => {
    if (!locations) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return locations;
    // State searches are exact: 2-letter code ("co") or full name ("colorado" / "colo...")
    if (needle.length === 2 && STATE_CODES.includes(needle)) {
      return locations.filter((l) => (l.state || "").toLowerCase() === needle);
    }
    return locations.filter((l) => {
      const stateName = STATE_NAMES[(l.state || "").toLowerCase()] || "";
      if (stateName && needle.length >= 3 && stateName.toLowerCase().startsWith(needle)) return true;
      return [l.company, l.city, l.zip, l.address].join(" ").toLowerCase().includes(needle);
    });
  }, [locations, q]);

  const byState = useMemo(() => {
    const groups = {};
    for (const l of filtered) {
      const key = l.state || "Other";
      (groups[key] = groups[key] || []).push(l);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  return (
    <div data-testid="dealer-locator-page">
      <PageHeader
        kicker="Dealer Locator"
        title="Find Your Local Sign Shop"
        sub="Sign shops in our authorized dealer network — have questions for us directly, call 970-650-5173."
        image="/assets/img/w-gallery-img-7057.jpg"
      />
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12">
        <Reveal>
          <div className="clip-corner overflow-hidden border border-girder bg-steel/50 p-2 sm:p-4">
            <DealerMap locations={q.trim() && filtered.length === 0 ? locations || [] : filtered} />
            <div className="relative" data-testid="dealer-search-wrap">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by company, city, state or ZIP..."
                data-testid="dealer-search"
                className="w-full border border-girder bg-obsidian py-3.5 pl-11 pr-4 text-sm text-slate-100 placeholder:text-slate-600 focus:border-hivis focus:outline-none"
              />
            </div>
            <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-slate-500" data-testid="dealer-count">
              {locations === null ? "Loading dealers..." : `${filtered.length} dealer location${filtered.length === 1 ? "" : "s"}${q ? ` matching "${q}"` : ""}`}
            </p>
          </div>
        </Reveal>

        <Reveal i={1}>
          <div className="mt-8 space-y-10" data-testid="dealer-list">
            {byState.map(([state, dealers]) => (
              <div key={state} data-testid={`dealer-state-${state.toLowerCase().replace(/\s+/g, "-")}`}>
                <p className="flex items-center gap-2 border-b border-girder pb-3 font-mono text-xs uppercase tracking-[0.25em] text-hivis">
                  <MapPin className="h-3.5 w-3.5" /> {state} <span className="text-slate-600">({dealers.length})</span>
                </p>
                <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {dealers.map((l) => (
                    <div key={l.id} className="clip-corner border border-girder bg-obsidian p-5" data-testid={`dealer-card-${l.id}`}>
                      <p className="font-display text-lg font-bold uppercase leading-tight text-slate-100">{l.company}</p>
                      {(l.address || l.city) && (
                        <p className="mt-2 text-sm text-slate-400">
                          {l.address && <>{l.address}<br /></>}
                          {[l.city, l.state].filter(Boolean).join(", ")} {l.zip}
                        </p>
                      )}
                      <div className="mt-3 space-y-1.5 text-sm">
                        {l.phone && (
                          <a href={`tel:${l.phone.replace(/[^+\d]/g, "")}`} className="flex items-center gap-2 text-slate-300 transition-colors hover:text-hivis" data-testid={`dealer-phone-${l.id}`}>
                            <Phone className="h-3.5 w-3.5 text-hivis" /> {l.phone}
                          </a>
                        )}
                        {l.website && (
                          <a href={l.website.startsWith("http") ? l.website : `https://${l.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 break-all text-slate-300 transition-colors hover:text-hivis" data-testid={`dealer-website-${l.id}`}>
                            <Globe className="h-3.5 w-3.5 shrink-0 text-hivis" /> {l.website.replace(/^https?:\/\//, "")}
                          </a>
                        )}
                        {l.email && (
                          <a href={`mailto:${l.email}`} className="flex items-center gap-2 text-slate-300 transition-colors hover:text-hivis" data-testid={`dealer-email-${l.id}`}>
                            <Mail className="h-3.5 w-3.5 text-hivis" /> {l.email}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {locations !== null && filtered.length === 0 && (
              <p className="py-10 text-center font-mono text-xs uppercase tracking-widest text-slate-600" data-testid="dealer-empty">
                No dealers match that search — call 970-650-5173 and we will point you to one
              </p>
            )}
          </div>
        </Reveal>

        <Reveal i={2}>
          <div className="mt-10 grid gap-5 md:grid-cols-2" data-testid="locator-fallback">
            <div className="clip-corner border border-girder bg-obsidian p-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-hivis">No dealer nearby?</p>
              <h3 className="mt-2 font-display text-2xl font-bold uppercase text-slate-100">We ship direct</h3>
              <p className="mt-2 text-sm text-slate-400">Order any kit online and we ship anywhere in the US — or call <a href="tel:9706505173" className="text-hivis">970-650-5173</a> (8am-5pm MST).</p>
              <Link to="/products" data-testid="locator-shop-link" className="clip-corner-sm mt-5 inline-flex items-center gap-2 bg-hivis px-5 py-3 font-display font-bold uppercase text-obsidian transition-colors hover:bg-hivis-hover">Shop the kits</Link>
            </div>
            <div className="clip-corner border border-girder bg-obsidian p-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-hivis">Buying for an organization?</p>
              <h3 className="mt-2 font-display text-2xl font-bold uppercase text-slate-100">Cities, schools & institutions</h3>
              <p className="mt-2 text-sm text-slate-400">Spec-ready displays for public spaces with quote and purchase-order options.</p>
              <Link to="/institutions" data-testid="locator-institutions-link" className="clip-corner-sm mt-5 inline-flex items-center gap-2 border border-girder px-5 py-3 font-display font-bold uppercase text-slate-200 transition-colors hover:border-hivis hover:text-hivis">Institutional quotes</Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
